/**
 * Inning-detail modal. Opens to a specific half-inning (top/bottom + number),
 * shows every play in that half with its pitch log, and supports Prev/Next
 * navigation through half-innings. Keyboard: ←/→ navigate, Esc closes.
 *
 * Reads from the live in-memory snapshot supplied by the parent — when the
 * SSE stream updates the snapshot, the modal updates too. The live field
 * under the modal stays alive and crisp (no blur on the backdrop).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { usePrefetchPlayers } from '../api/usePrefetchPlayers';
import {
  selectInningClickableIds,
  selectInningRange,
  selectPlaysByInning,
  type PlaySummary,
  type StatcastMap,
} from '../state/selectors';
import MiniHitDiamond from './MiniHitDiamond';
import Modal from './Modal';
import { isTopModal, popModal, pushModal } from './modalStack';
import StrikeZone from './StrikeZone';
import ZoomModal from './ZoomModal';
import styles from './InningDetailModal.module.css';

export interface InningModalTarget {
  inning: number;
  half: 'top' | 'bottom';
  /** Optional atBatIndex to scroll/highlight on open. */
  atBatIndex?: number;
}

interface InningDetailModalProps {
  open: boolean;
  target: InningModalTarget | null;
  snapshot: Record<string, unknown> | null;
  statcast?: StatcastMap;
  onClose: () => void;
  /** Optional: opens a player profile (used by per-pitch batter/pitcher names). */
  onOpenPlayer?: (seed: { id: number; name?: string | null; jersey?: string | null; position?: string | null }) => void;
  /** Passed through to player-stats prefetch hook so click→modal is instant. */
  gamePk?: number;
}

const ORDINAL: Record<number, string> = {
  1: '1st', 2: '2nd', 3: '3rd', 4: '4th', 5: '5th',
  6: '6th', 7: '7th', 8: '8th', 9: '9th',
};
function ordinal(n: number): string {
  if (n in ORDINAL) return ORDINAL[n]!;
  if (n % 10 === 1 && n % 100 !== 11) return `${n}st`;
  if (n % 10 === 2 && n % 100 !== 12) return `${n}nd`;
  if (n % 10 === 3 && n % 100 !== 13) return `${n}rd`;
  return `${n}th`;
}

function titleFor(target: InningModalTarget): string {
  const side = target.half === 'top' ? 'Top' : 'Bottom';
  return `${side} ${ordinal(target.inning)}`;
}

function nextHalf(target: InningModalTarget, maxInning: number): InningModalTarget | null {
  if (target.half === 'top') return { inning: target.inning, half: 'bottom' };
  if (target.inning >= maxInning) return null;
  return { inning: target.inning + 1, half: 'top' };
}

function prevHalf(target: InningModalTarget): InningModalTarget | null {
  if (target.half === 'bottom') return { inning: target.inning, half: 'top' };
  if (target.inning <= 1) return null;
  return { inning: target.inning - 1, half: 'bottom' };
}

export default function InningDetailModal({
  open,
  target,
  snapshot,
  statcast,
  onClose,
  onOpenPlayer,
  gamePk,
}: InningDetailModalProps): JSX.Element | null {
  // Local current target so Prev/Next don't bounce back to the initial target.
  const [current, setCurrent] = useState<InningModalTarget | null>(target);
  // Highlight only the at-bat the modal was opened *at* — once the user
  // navigates Prev/Next away, the highlight is cleared so the at-bat ring
  // never floats over an unrelated inning's play.
  const [highlightedAtBatIndex, setHighlightedAtBatIndex] = useState<number | null>(
    target?.atBatIndex ?? null,
  );
  const initialAtBatRef = useRef<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset when the modal opens with a fresh target.
  useEffect(() => {
    if (open && target) {
      setCurrent(target);
      setHighlightedAtBatIndex(target.atBatIndex ?? null);
      initialAtBatRef.current = target.atBatIndex ?? null;
    }
  }, [open, target]);

  const range = useMemo(() => selectInningRange(snapshot), [snapshot]);
  const plays = useMemo<PlaySummary[]>(
    () => (current ? selectPlaysByInning(snapshot, current.inning, current.half, statcast) : []),
    [snapshot, current, statcast],
  );

  // Prefetch player stats for every batter/pitcher in the visible half-inning
  // so clicking a name inside this modal opens PlayerModal with data already
  // cached — no "Player {id}" placeholder.
  const inningIds = useMemo(
    () => (current && open ? selectInningClickableIds(snapshot, current.inning, current.half) : []),
    [snapshot, current, open],
  );
  usePrefetchPlayers(gamePk, inningIds);

  // Scroll to the requested at-bat after render.
  useEffect(() => {
    if (!open || initialAtBatRef.current == null) return;
    const id = initialAtBatRef.current;
    requestAnimationFrame(() => {
      const node = listRef.current?.querySelector(`[data-atbatindex="${id}"]`);
      if (node && 'scrollIntoView' in node) {
        (node as HTMLElement).scrollIntoView({ behavior: 'auto', block: 'center' });
      }
      initialAtBatRef.current = null;
    });
  }, [open, plays.length]);

  // Internal navigation always clears the inbound at-bat highlight.
  function goTo(t: InningModalTarget): void {
    setCurrent(t);
    setHighlightedAtBatIndex(null);
  }

  // Modal-stack registration so the arrow-key handler below only fires when
  // the inning modal is the topmost (not when a PlayerModal/ZoomModal is
  // stacked above it).
  const stackToken = useRef<object>({});
  useEffect(() => {
    if (!open) return undefined;
    const token = stackToken.current;
    pushModal(token);
    return () => popModal(token);
  }, [open]);

  // Arrow-key navigation.
  useEffect(() => {
    if (!open || !current) return undefined;
    function onKey(e: KeyboardEvent): void {
      if (!isTopModal(stackToken.current)) return;
      if (e.key === 'ArrowRight') {
        const n = current && nextHalf(current, range.maxInning);
        if (n) {
          e.preventDefault();
          goTo(n);
        }
      } else if (e.key === 'ArrowLeft') {
        const p = current && prevHalf(current);
        if (p) {
          e.preventDefault();
          goTo(p);
        }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, current, range.maxInning]);

  // Zoom state for the per-play visualizations.
  const [zoom, setZoom] = useState<
    | { kind: 'strike'; pitches: PlaySummary['pitches'] }
    | { kind: 'hit'; hitData: NonNullable<PlaySummary['hitData']> }
    | null
  >(null);

  if (!open || !current) return null;

  const prev = prevHalf(current);
  const next = nextHalf(current, range.maxInning);

  return (
    <Modal open={open} onClose={onClose} ariaLabel="Inning detail" size="lg" fixedHeight testid="inning-modal">
      <header className={styles.header}>
        <div className={styles.nav}>
          <button
            type="button"
            onClick={() => prev && goTo(prev)}
            disabled={!prev}
            aria-label="Previous half-inning"
            data-testid="inning-modal-prev"
            className={styles.navBtn}
          >
            ←
          </button>
          <h2 className={styles.title} data-testid="inning-modal-title">
            {titleFor(current)}
          </h2>
          <button
            type="button"
            onClick={() => next && goTo(next)}
            disabled={!next}
            aria-label="Next half-inning"
            data-testid="inning-modal-next"
            className={styles.navBtn}
          >
            →
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close inning detail"
          data-testid="inning-modal-close"
          className={styles.close}
        >
          ×
        </button>
      </header>
      <div ref={listRef} className={styles.list}>
        {plays.length === 0 ? (
          <p className={styles.empty} data-testid="inning-modal-empty">
            No plays in this half-inning yet.
          </p>
        ) : (
          plays.map((play) => (
            <article
              key={play.atBatIndex}
              data-atbatindex={play.atBatIndex}
              data-highlighted={play.atBatIndex === highlightedAtBatIndex ? 'true' : 'false'}
              data-testid={`inning-modal-play-${play.atBatIndex}`}
              className={`${styles.play} ${
                play.atBatIndex === highlightedAtBatIndex ? styles.playHighlight : ''
              }`}
            >
              <div className={styles.playHeader}>
                <span className={styles.event}>{play.event ?? '—'}</span>
                {play.batterName && (
                  <button
                    type="button"
                    className={styles.batterLink}
                    onClick={() =>
                      play.batterId &&
                      onOpenPlayer?.({
                        id: play.batterId,
                        name: play.batterName,
                        jersey: play.batterJersey,
                      })
                    }
                    disabled={!onOpenPlayer || !play.batterId}
                  >
                    {play.batterName}
                  </button>
                )}
                {play.pitcherName && (
                  <span className={styles.vs}>
                    vs{' '}
                    <button
                      type="button"
                      className={styles.batterLink}
                      onClick={() =>
                        play.pitcherId &&
                        onOpenPlayer?.({
                          id: play.pitcherId,
                          name: play.pitcherName,
                          jersey: play.pitcherJersey,
                        })
                      }
                      disabled={!onOpenPlayer || !play.pitcherId}
                    >
                      {play.pitcherName}
                    </button>
                  </span>
                )}
              </div>
              {play.description && <p className={styles.desc}>{play.description}</p>}
              <div className={styles.playBody}>
                <div className={styles.playPitches}>
                  {play.pitches.length > 0 && (
                    <ol className={styles.pitches}>
                      {play.pitches.map((pitch) => (
                        <li
                          key={pitch.playId ?? `${pitch.index}-${pitch.displayNumber}`}
                          className={styles.pitch}
                        >
                          <span className={styles.pitchIndex}>{pitch.displayNumber}</span>
                          <span className={styles.pitchType}>{pitch.type}</span>
                          <span className={styles.pitchSpeed}>
                            {pitch.speed != null ? `${pitch.speed.toFixed(1)} mph` : '—'}
                          </span>
                          <span className={styles.pitchResult}>{pitch.result}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
                <div className={styles.playVisuals}>
                  <StrikeZone
                    pitches={play.pitches}
                    onZoom={() => setZoom({ kind: 'strike', pitches: play.pitches })}
                  />
                  {play.hitData && (
                    <MiniHitDiamond
                      x={play.hitData.x}
                      y={play.hitData.y}
                      trajectory={play.hitData.trajectory}
                      hardness={play.hitData.hardness}
                      onZoom={() => setZoom({ kind: 'hit', hitData: play.hitData! })}
                    />
                  )}
                </div>
              </div>
            </article>
          ))
        )}
      </div>
      <ZoomModal
        open={zoom !== null}
        onClose={() => setZoom(null)}
        ariaLabel={zoom?.kind === 'strike' ? 'Pitch locations zoom' : 'Ball landing zoom'}
      >
        {zoom?.kind === 'strike' && (
          <StrikeZone pitches={zoom.pitches} expanded />
        )}
        {zoom?.kind === 'hit' && (
          <MiniHitDiamond
            x={zoom.hitData.x}
            y={zoom.hitData.y}
            trajectory={zoom.hitData.trajectory}
            hardness={zoom.hitData.hardness}
            expanded
          />
        )}
      </ZoomModal>
    </Modal>
  );
}
